/**
 * useMeterTokenColors — the meter's reactive token-color bag.
 *
 * Carved out of `useMeterGeometry.ts` per FA-2 §3.2 (AH.W5-b.2c): the
 * sibling `meterGeometry.ts` is honestly pure (it never calls a Vue
 * primitive), so the setup-time wiring that mints reactive refs lives
 * here. The renderer shell instantiates this once at setup and passes
 * the result into `recomputeGeometry()` whenever the canvas rebuilds.
 *
 * Two token colours: `--meter-track-stroke` (the ring backdrop) and
 * `--meter-dial-color` (the needle). Both come from glass-ui's
 * `useTokenColor`, which exposes a `.value` ref-of-string plus a
 * `refresh()` knob. The shell calls `refresh()` inside
 * `recomputeGeometry` so post-mount cascade mutations (DPR change,
 * container-query rebinding, theme flip) re-resolve before the ring +
 * dial shapes rebuild.
 */

import { useTokenColor } from "@mkbabb/glass-ui/dom";

/**
 * Reactive token-color refs the shell uses to drive `meter-track-stroke`
 * + `meter-dial-color` cascades. Both expose `.value.value` (the
 * underlying ref-of-string) plus a `refresh()` knob — the shell calls
 * the latter inside `recomputeGeometry` so post-mount cascade mutations
 * (rare; DPR change + container-query rebinding) re-resolve before the
 * ring/dial shapes rebuild.
 */
export interface MeterTokenColors {
    track: ReturnType<typeof useTokenColor>;
    dial: ReturnType<typeof useTokenColor>;
}

export function useMeterTokenColors(): MeterTokenColors {
    return {
        track: useTokenColor("--meter-track-stroke"),
        dial: useTokenColor("--meter-dial-color"),
    };
}
